import axios from 'axios';
import { createAsyncThunk } from '@reduxjs/toolkit';
import { AsyncTaskType } from '../../types/async-task/async-task.type';
import {
  CreateDialerDto,
  DialerDetailsType,
  DialerListItemType,
  DialerType,
  UpdateDialerDto,
} from '../../types/dialer/dialer.type';
import { PaginationType } from '../../types/utils/pagination.type';
import { RouteParamsType } from '../../types/utils/router-params.type';
import { additionalQueryParams } from '../../utils/utilities';
import { setShowModal, setModalContent } from '../utils/utils.slice';
import { rejectWithApiError } from '../../utils/api';
import { RootReducerState } from '../../redux/Reducers';

export const postDialers = createAsyncThunk<
  AsyncTaskType,
  {
    data: {
      equipment_group: { id: number };
      dialers: Array<
        Pick<
          DialerType,
          | 'radio_id'
          | 'description'
          | 'brand'
          | 'model'
          | 'cs_account'
          | 'cs_primary_receiver_country_code'
          | 'cs_primary_receiver'
          | 'cs_secondary_receiver_country_code'
          | 'cs_secondary_receiver'
          | 'config_key'
        >
      >;
    };
  },
  { state: RootReducerState }
>('dialers/postDialers', async ({ data }, thunkAPI) => {
  try {
    const response = await axios.post<AsyncTaskType>(`${process.env.REACT_APP_API_URL}/dialer/bulk`, data);
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during creating dialers!' });
  }
});

export const postDialerDetails = createAsyncThunk<
  DialerDetailsType,
  { data: CreateDialerDto },
  { state: RootReducerState }
>('dialers/postDialerDetails', async ({ data }, thunkAPI) => {
  try {
    const response = await axios.post<DialerDetailsType>(`${process.env.REACT_APP_API_URL}/dialer`, data);
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during creating dialer!' });
  }
});

export const getDialersList = createAsyncThunk<
  PaginationType<DialerListItemType>,
  RouteParamsType & { controller?: AbortController },
  { state: RootReducerState }
>('dialers/getDialersList/no-loader', async (data, thunkAPI) => {
  try {
    const { controller, ...params } = data;

    const additionalParams = additionalQueryParams(
      thunkAPI.getState().sharedReducer.selectedCompany,
      thunkAPI.getState().sharedReducer.selectedLocation,
      thunkAPI.getState().sharedReducer.includeSuborgs,
      thunkAPI.getState().sharedReducer.selectedCountry
    );
    const mergedParams = { ...params, ...additionalParams };
    const queryString = Object.keys(mergedParams)
      .map(key => key + '=' + mergedParams[key])
      .join('&');

    const response = await axios.get<PaginationType<DialerListItemType>>(
      `${process.env.REACT_APP_API_URL}/dialer${queryString ? `?${queryString}` : ''}`,
      controller ? { signal: controller.signal } : undefined
    );
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during fetching dialers!' });
  }
});

export const getDialerDetails = createAsyncThunk<DialerDetailsType, { id: number }, { state: RootReducerState }>(
  'dialers/getDialerDetails',
  async ({ id }, thunkAPI) => {
    try {
      const response = await axios.get<DialerDetailsType>(`${process.env.REACT_APP_API_URL}/dialer/${id}`);
      return response.data;
    } catch (error) {
      return rejectWithApiError({ thunkAPI, error, fallback: 'Error during fetching dialer!' });
    }
  }
);

export const patchDialerDetails = createAsyncThunk<
  DialerDetailsType,
  { id: number; data: UpdateDialerDto },
  { state: RootReducerState }
>('dialers/patchDialerDetails', async ({ data, id }, thunkAPI) => {
  try {
    const response = await axios.patch<DialerDetailsType>(`${process.env.REACT_APP_API_URL}/dialer/${id}`, data);
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during updating dialer!' });
  }
});

export const patchEnabledDialerDetails = createAsyncThunk<
  DialerDetailsType,
  { id: number; enabled: boolean },
  { state: RootReducerState }
>('dialers/patchEnabledDialerDetails', async ({ enabled, id }, thunkAPI) => {
  try {
    const response = await axios.patch<DialerDetailsType>(
      `${process.env.REACT_APP_API_URL}/dialer/${id}/toggle-enabled`,
      { enabled }
    );
    thunkAPI.dispatch(getDialerDetails({ id }));
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during updating dialer!' });
  }
});

export const deleteDialerDetails = createAsyncThunk<
  DialerDetailsType,
  { id: number; fetchList: boolean },
  { state: RootReducerState }
>('dialers/deleteDialerDetails', async ({ id, fetchList }, thunkAPI) => {
  try {
    const response = await axios.delete<DialerDetailsType>(`${process.env.REACT_APP_API_URL}/dialer/${id}`);
    if (fetchList) thunkAPI.dispatch(getDialersList(thunkAPI.getState().dialersReducer.dialersRouteParams));
    thunkAPI.dispatch(setShowModal(false));
    thunkAPI.dispatch(setModalContent(null));
    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during deleting dialer!' });
  }
});

export const postDialerFixService = createAsyncThunk<
  { success: boolean },
  { id: number; fetchList: boolean },
  { state: RootReducerState }
>('dialers/postDialerFixService', async ({ id, fetchList }, thunkAPI) => {
  try {
    const response = await axios.post<{
      success: boolean;
    }>(`${process.env.REACT_APP_API_URL}/dialers/${id}/fix-service`, {});

    if (fetchList) thunkAPI.dispatch(getDialersList(thunkAPI.getState().dialersReducer.dialersRouteParams));
    else thunkAPI.dispatch(getDialerDetails({ id }));

    return response.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during fixing dialer service!' });
  }
});

export const postDialerOwnershipTransfer = createAsyncThunk<
  void,
  {
    id: number;
    data: {
      owner: { id: number };
    };
  },
  { state: RootReducerState; rejectValue: string }
>('dialers/postDialerOwnershipTransfer', async ({ id, data }, thunkAPI) => {
  try {
    if (!data.owner?.id) {
      return rejectWithApiError({ thunkAPI, error: null, fallback: 'Owner organization is required!' });
    }

    await axios.post(`${process.env.REACT_APP_API_URL}/dialer/${id}/ownership-transfer`, data);
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Error during Dialer ownership transfer!' });
  }
});
